import memory from'../../../pip/library/pi-memory/pi-memory.js';

import { TLocationFields, TParameters } from '../types';
import { uriN } from './uri';

export interface INavigationLocation extends TLocationFields {
  /**
   * every sub-path of the uri
   *
   * @example
   *
   * /a/b/c/d
   *
   * nth.0 = /a/b/c/d
   * nth.1 = /a/b/c
   * nth.2 = /a/b
   * nth.3 = /a
   * nth.4 = /
   */
  nth: string[];
  /**
   * from three sources parameters:
   * - previous parameters from the last update
   * - query parameters
   * - new parameters pushed on state
   */
  parameters: TParameters;
  /**
   * parameters from the query string
   */
  query_parameters: TParameters;
  /**
   * parameters from the route calculation
   */
  route_parameters: TParameters;
  /**
   * the normalized hash
   */
  base_hash: string;
  /**
   * the normalized hash from the last state update
   */
  previous_hash: string;
  /**
   *
   */
  top_hash: string;
  /**
   *
   */
  parent_hash: string;
}

/**
 * a static class which mirrors the location, it updates when the application
 * navigation state changes
 *
 * @note from ai-location
 */
export class NavigationLocation {
  private __window__onHashChangeBound: (e: HashChangeEvent) => void;

  /**
   *
   */
  regExp = {
    repeatingSlashes: /\/+/g,
    removeQuery: /\?.*?$/,
    firstSlash: /\/$/,
    lastSlash: /^\//
  };

  /**
   * @returns the active location state
   */
  get active() {
    return memory.shared<INavigationLocation>({}, 'hip-location');
  }

  /**
   * only the location update functions should update the active location
   */
  protected set _active(state: INavigationLocation) {
    memory.shared<INavigationLocation>({}, 'hip-location', state);
  }

  /**
   *
   */
  constructor() {
    this.update();
  }

  /**
   * add hash-change listener
   */
  startup() {
    this.shutdown();

    this.__window__onHashChangeBound = this.__window__onHashChange.bind(this);
    window.addEventListener('hashchange', this.__window__onHashChangeBound, true);
  }

  /**
   * remove hash-change listener
   */
  shutdown() {
    if (this.__window__onHashChangeBound) {
      window.removeEventListener('hashchange', this.__window__onHashChangeBound, true);
    }
  }

  /**
   * on hash change update the location
   */
  private __window__onHashChange() {
    this.update();
  }

  /**
   * remove all but the base client-path from a client route
   *
   * @example
   *
   * ```typescript
   * #/a//b///c/?d=1&2[]=dd -> a/b/c
   * ```
   */
  normalize(hash: string): string {
    const regExp = this.regExp;
    return `${hash}`
      .replace(regExp.removeQuery, '')
      .replace(regExp.repeatingSlashes, '/')
      .replace('#', '')
      .replace(regExp.lastSlash, '')
      .replace(regExp.firstSlash, '');
  }

  /**
   * reflect the `window.location` along with accepting any parameters from
   * the router
   */
  update(
    parameters: TParameters = {},
    location: TLocationFields = window.location || ({ hash: '' } as TLocationFields)
  ): INavigationLocation {
    const L: INavigationLocation = this.__reflectLocation(location);

    L.query_parameters = (uriN.readParameters(location.href || '') ?? {}) as Record<string, string | number | boolean>;
    L.route_parameters = parameters ?? {};

    // allow parameters in uri to apply over internal parameters, which can be used as defaults
    //
    L.parameters = Object.assign({}, L.query_parameters, L.route_parameters);
    return (this._active = L);
  }

  /**
   * reflect the `window.location` and push the existing parameters from the router on to the
   * current location
   */
  reflect(location: TLocationFields = window.location || ({ hash: '' } as TLocationFields)): INavigationLocation {
    const active = this.active;
    const L: INavigationLocation = this.__reflectLocation(location);

    L.query_parameters = active.query_parameters;
    L.route_parameters = active.route_parameters;
    L.parameters = active.parameters;
    return (this._active = L);
  }

  /**
   * reflect all internal state from the `window.location`
   */
  private __reflectLocation(location: TLocationFields): INavigationLocation {
    const active = this.active;
    const L: INavigationLocation = {
      ...JSON.parse(JSON.stringify(location)),
      nth: {}
    };

    const base_hash = this.normalize(L.hash);
    const hash_list = base_hash.split('/');

    const nthTop = hash_list.length - 1;
    let nth = '';
    hash_list.forEach((part, index) => {
      nth = `${nth}/${part}`;
      // @deprecated, we use nth[#] because it is easier to describe in TS
      L[`nth_${nthTop - index}`] = `#${nth}`;
      L.nth[`${nthTop - index}`] = `#${nth}`;
    });

    L.previous_hash = active.base_hash || '';
    L.base_hash = `#/${base_hash}`;

    // @note this is dangerous code, order matters because the `pop`
    // influences the string joined, because the hash_list is changed
    //
    L.top_hash = `${hash_list.pop() || ''}`;
    L.parent_hash = `#/${hash_list.join('/')}`;

    // @deprecated support from ai-location
    L['original_hash'] = base_hash;
    return L;
  }

  /**
   * same as `window.location.reload`
   */
  reload() {
    if (window.location) {
      window.location.reload();
    }

    return this;
  }

  /**
   * same as assigning `location.href` but we make sure the uriN is safe
   */
  goto(uri: string, params?: TParameters): string {
    if (window.location) {
      window.location.href = uriN.write(uri, params);
    }
    return uri;
  }

  /**
   * same as `history.back`
   */
  back() {
    if (window.history) {
      window.history.back();
    }
    return this;
  }

  /**
   * same as `history.forward`
   */
  forward() {
    if (window.history) {
      window.history.forward();
    }
    return this;
  }

  /**
   * we return a normalized hash of the current location
   *
   * @note copied from AHP
   * @see #normalize
   */
  activeRoute(loc: { hash: string } = window.location || { hash: '' }) {
    return this.normalize(loc.hash);
  }
}

export const locationN = new NavigationLocation();
